import React from 'react';
import { Button, Space, Tooltip } from 'antd';

import { formatCount } from '../../utils/counts';

/**
 * The selection bar: what is selected, and the verbs that act on it.
 *
 * Desktop — a bar under the filter row, in platform dress (the shape
 * `AdminUsers` already uses for the same job). Phone — a bottom bar that
 * REPLACES the page's FAB while selection mode is on, because both own the
 * bottom-right and a toolbar never scrolls sideways (§5 Lists). The FAB
 * suppression is the page's; this component only has to fit the slot.
 *
 * Tags… (docs/plans/tags.md §5.2) adds or removes tags on the selection —
 * collection tags or my private tags — with a check first (ItemsTagsPanel).
 * It is a record verb, after Set a value…; it is never disabled for want of
 * write access, because private tags need only read.
 *
 * Attach files… (KPR-187, BulkAttachPanel) puts many files on the selected
 * items in one pass, each file matched to its item. A record verb, after
 * Tags…; it appears only where the collection takes files (the page passes
 * `onAttach` then).
 *
 * Change card (KPR-710) opens the Change card page for the selection: the
 * picked rows, or the list filter's matches ("Change card for all N"). A
 * record verb after Attach files…; a selection it cannot take is shut with the
 * reason (more than one card, a merged list, a whole card for a non-manager).
 *
 * Triage — Accept… and Reject… — appears only when the page passes
 * `onAccept` / `onReject`, i.e. when the list is showing intake submissions
 * waiting for review. Accept is a record verb and leads; Reject deletes, so
 * it sits with Delete at the end, red (the batch-action order, §3).
 *
 * "Select all N matching" is offered only when there are more matches than
 * the feed has loaded — otherwise "select everything here" and "select
 * everything matching" are the same gesture said twice, and the second one
 * carries a server-side by-filter apply the first does not need.
 */
export default function ItemsSelectionBar({
	count,
	allMatching,
	totalCount = 0,
	loadedCount = 0,
	canOfferAllMatching = true,
	onSelectAllMatching,
	onSelectVisible,
	onClear,
	onSetValue,
	onTags = null,
	onAttach = null,
	// Change card (KPE-028, KPR-710): the selection to another card. Always
	// offered when the page passes it; shut, with the reason, when it cannot go.
	onChangeCard = null,
	changeCardLabel = 'Change card',
	changeCardDisabled = false,
	changeCardReason = null,
	onDelete,
	// Move to… — another collection of this one's family (sub-collections).
	// Absent outside a family.
	onMove = null,
	moveDisabled = false,
	moveReason = null,
	onAccept = null,
	onReject = null,
	setValueDisabled = false,
	setValueReason = null,
	deleteDisabled = false,
	deleteReason = null,
	variant = 'bar',
}) {
	if (!count) return null;

	const moreMatchingThanLoaded = totalCount > loadedCount && totalCount > count;
	const showAllMatching = canOfferAllMatching && !allMatching && moreMatchingThanLoaded;

	const label = allMatching
		? `All ${formatCount(count, 'item') || `${count} items`} matching this filter`
		: `${formatCount(count, 'item') || `${count} items`} selected`;

	const action = (key, onClick, text, { disabled, reason, danger } = {}) => {
		const button = (
			<Button
				key={key}
				size="small"
				danger={danger}
				disabled={disabled}
				onClick={onClick}
				data-action={key}
			>
				{text}
			</Button>
		);
		// A disabled button that does not say why is a dead end. The reason is
		// always a fact about the SELECTION ("these items use three different
		// cards"), never a scolding.
		// Hover, focus AND press: a touch has no hover, and a disabled button takes
		// no press itself, so the wrapper takes it (KPR-710).
		return disabled && reason ? (
			<Tooltip key={key} title={reason} trigger={['hover', 'focus', 'click']}>
				<span tabIndex={0} role="button" aria-disabled="true" aria-label={`${text}. ${reason}`} data-action={`${key}-reason`}>{button}</span>
			</Tooltip>
		) : button;
	};

	return (
		<div
			className={`items-selection-bar items-selection-bar--${variant}`}
			role="region"
			aria-label="Selected items"
			data-testid="items-selection-bar"
		>
			<span className="items-selection-bar__count">{label}</span>
			{showAllMatching ? (
				<Button size="small" type="link" onClick={onSelectAllMatching} data-action="select-all-matching">
					Select all {totalCount} matching
				</Button>
			) : null}
			{!allMatching && onSelectVisible && loadedCount > count ? (
				<Button size="small" type="link" onClick={onSelectVisible} data-action="select-visible">
					Select all {loadedCount} here
				</Button>
			) : null}
			<span className="items-selection-bar__spacer" />
			<Space size={4} wrap={false}>
				{onAccept ? action('accept', onAccept, 'Accept…') : null}
				{action('set-value', onSetValue, 'Set a value…', { disabled: setValueDisabled, reason: setValueReason })}
				{onTags ? action('tags', onTags, 'Tags…') : null}
				{onAttach ? action('attach', onAttach, 'Attach files…') : null}
				{onChangeCard ? action('change-card', onChangeCard, changeCardLabel, { disabled: changeCardDisabled, reason: changeCardReason }) : null}
				{onMove ? action('move', onMove, 'Move to…', { disabled: moveDisabled, reason: moveReason }) : null}
				{onReject ? action('reject', onReject, 'Reject…', { danger: true }) : null}
				{action('delete', onDelete, 'Delete…', { disabled: deleteDisabled, reason: deleteReason, danger: true })}
				<Button size="small" type="text" onClick={onClear} data-action="clear-selection">Clear</Button>
			</Space>
		</div>
	);
}
